import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from "../lib/apiClient";

// Types
export interface SRSSentence {
  id: string;
  sentence: string;
  translation: string;
  srs_interval: number;
  srs_repetition: number;
  srs_easiness: number;
  srs_due_date: string;
  srs_last_reviewed?: string;
  srs_review_count: number;
  words: {
    word: string;
    language: string;
  };
}

export interface SRSReviewStats {
  totalReviews: number;
  reviewsToday: number;
  reviewsThisWeek: number;
  averageDifficulty: number;
  retentionRate: number;
  dueCount: number;
  overdueCount: number;
  matureCount: number;
}

export interface SRSReview {
  sentenceId: string;
  difficulty: number; // 0-5 scale
  responseTime?: number; // milliseconds
}

export interface SRSData {
  interval: number;
  repetition: number;
  easiness: number;
  due_date: string;
}

export function getUrgencyLevel(dueDate: string): 'overdue' | 'due' | 'soon' | 'future' {
  if (!dueDate) return 'future';
  const now = new Date();
  const due = new Date(dueDate);
  const diffHours = (due.getTime() - now.getTime()) / (1000 * 60 * 60);

  if (diffHours < 0) return 'overdue';
  if (diffHours < 24) return 'due';
  if (diffHours < 72) return 'soon';
  return 'future';
}

export function getUrgencyColor(urgency: string): string {
  switch (urgency) {
    case 'overdue': return 'red';
    case 'due': return 'orange';
    case 'soon': return 'yellow';
    default: return 'green';
  }
}

export function useSRS() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Get due reviews
  const getDueReviews = async (limit: number = 20): Promise<SRSSentence[]> => {
    if (!user) throw new Error("User not authenticated");

    const result = await apiClient.getDueSentences(limit);

    // Map result to SRSSentence if needed (assuming backend returns correct shape)
    // The backend returns snake_case or camelCase?
    // Drizzle schema uses camelCase for TS, snake_case for DB.
    // The API probably returns camelCase properties if Drizzle's select() was used without mapping?
    // Wait, Drizzle select().from(sentences).all() returns objects with keys matching the schema definition keys.
    // In schema.ts:
    // srsInterval: integer('srs_interval')
    // So the returned object has srsInterval, not srs_interval.
    // But Play.tsx expects snake_case (srs_interval) in SRSSentence interface.
    // I need to map it.

    return result.map((s: any) => ({
      ...s,
      // Map camelCase to snake_case for frontend compatibility
      srs_interval: s.srsInterval || s.srs_interval,
      srs_repetition: s.srsRepetition || s.srs_repetition,
      srs_easiness: s.srsEasiness || s.srs_easiness,
      srs_due_date: s.srsDueDate || s.srs_due_date,
      srs_last_reviewed: s.srsLastReviewed || s.srs_last_reviewed,
      srs_review_count: s.srsReviewCount || s.srs_review_count,
      times_seen: s.timesSeen || s.times_seen,
      word_ids: s.wordIds || s.word_ids,
      // Handle words relation if it's not joined
      // Backend sentencesRoutes.get('/') doesn't join words.
      // But srsRoutes.get('/due') also doesn't join words!
      // This is a PROBLEM. Play.tsx needs words.word.

      // I need to check srsRoutes.get('/due') implementation again.
    }));
  };

  // Submit a review
  const submitReviewMutation = useMutation({
    mutationFn: async (review: SRSReview) => {
      if (!user) throw new Error("User not authenticated");

      return await apiClient.recordReview(
        parseInt(review.sentenceId), // Ensure ID is number
        review.difficulty,
        review.responseTime
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["srs-due-reviews"] });
      queryClient.invalidateQueries({ queryKey: ["srs-stats"] });
    },
  });

  const submitReview = async (review: SRSReview) => {
    return submitReviewMutation.mutateAsync(review);
  };

  // Get stats
  const useReviewStats = () => {
    return useQuery({
      queryKey: ["srs-stats"],
      queryFn: async () => {
        if (!user) return null;
        return await apiClient.getReviewStats();
      },
      enabled: !!user,
    });
  };

  // Hook for getting due reviews
  const useDueReviews = (limit: number = 20) => {
    return useQuery({
      queryKey: ["srs-due-reviews", limit],
      queryFn: () => getDueReviews(limit),
      enabled: !!user,
      staleTime: 1000 * 60 * 5, // 5 minutes
    });
  };

  // Bulk review mutation (placeholder if not used or needed)
  const scheduleBulkReviewMutation = useMutation({
    mutationFn: async (reviews: SRSReview[]) => {
      if (!user) throw new Error("User not authenticated");
      // Execute sequentially or parallel
      return Promise.all(
        reviews.map((r) => submitReviewMutation.mutateAsync(r))
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["srs-due-reviews"] });
      queryClient.invalidateQueries({ queryKey: ["srs-stats"] });
    },
  });

  return {
    getDueReviews,
    submitReview,
    submitReviewMutation,
    useReviewStats,
    useDueReviews,
    scheduleBulkReviewMutation,
  };
}
